import styled from '@emotion/styled';
import { Breadcrumb, Button, Drawer, Input, Table } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { FaArrowLeft, FaHome, FaPlus, FaSitemap, FaUniversity, FaUsers } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AgenceEntity } from '../../../../entities/agence.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { ROUTES } from '../../../../routes';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { AgenceForm } from '../components/AgenceForm';
import { SideBar } from '../components/SidebarItems';
import { getCustomUtilisateurs } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  display: flex;
  color: var(--mt-text);
  font-family: Poppins-R;

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .style-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
    }
  }

  .link {
    text-decoration: underline;
    cursor: pointer;
  }

  > .content {
    flex: 1;
    min-width: 0;
    padding: 12px;
    background: transparent;
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .admin-hero p {
    max-width: 740px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
  }

  .back-button,
  .primary-action {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .page-toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .page-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 360px) 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-shell {
    overflow: hidden;
    padding: 12px;
  }

  .ant-table {
    background: transparent;
    color: var(--mt-text);
  }

  @media (max-width: 900px) {
    flex-direction: column;

    > .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .page-toolbar {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const AgencePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const partner = (useLocation().state as any)?.partner;
  const [agences, setAgences] = useState<AgenceEntity[]>();
  const [filterAgences, setFilterAgences] = useState<AgenceEntity[]>();
  const [users, setUsers] = useState<UserEntity[]>([]);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  useEffect(() => {
    if (partner) {
      setAgences(partner.agences);
      setFilterAgences(partner.agences);
    } else {
      history.push(ROUTES.PANEL.ADMIN.PARTENAIRE);
    }

    getCustomUtilisateurs(connectedUser.id).then(({ data }) => {
      setUsers(data);
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const COLUMNS = [
    {
      title: 'Code Agence',
      dataIndex: 'code',
    },
    {
      title: 'Agence',
      dataIndex: 'nom',
    },
    {
      title: 'Localisation',
      dataIndex: 'localisation',
    },
    {
      title: "Nombre d'utilisateur",
      render: (cell: any, row: AgenceEntity) => {
        return `${users.filter((u) => u.agence === row.url).length || 0}`;
      },
    },
  ];

  return (
    <Layout hideYellowScrollInfo>
      <Container>
        <SideBar defaultOpenKeys={['m1']} current='1.1' />{' '}
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <Breadcrumb separator='>' className='bread-crumb'>
            <Breadcrumb.Item>
              <FaHome style={{ marginRight: 5 }} />
              <span>Administration</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => history.push(ROUTES.PANEL.ADMIN.PARTENAIRE)}
            >
              <span className='link'>Partenaires</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Agences</span>
            </Breadcrumb.Item>
          </Breadcrumb>

          <section className='admin-hero'>
            <div>
              <span className='eyebrow'>Administration</span>
              <h1>Agences - {partner?.nom}</h1>
              <p>
                Supervisez les agences rattachées, leur localisation et les
                utilisateurs opérant sur le périmètre partenaire.
              </p>
            </div>
            <Button className='back-button' icon={<FaArrowLeft />} onClick={() => history.goBack()}>
              Retour
            </Button>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Agences' value={filterAgences?.length || '--'} icon={<FaUniversity />} />
            <KpiCard label='Utilisateurs' value={users.length || '--'} icon={<FaUsers />} />
            <KpiCard label='Partenaire' value={partner?.code_referent || partner?.nom || '--'} icon={<FaSitemap />} />
          </section>

          <div className='page-toolbar'>
            <div className='search-box'>
              <FiSearch />
              <Input
                placeholder='Rechercher...'
                onChange={(e) => {
                  setFilterAgences(
                    agences?.filter(
                      (age) =>
                        age.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        age.localisation
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
            </div>
            <span />

              <Button
                icon={<FaPlus />}
                className='primary-action'
                onClick={() => setShowDrawer(true)}
              >
                Nouvel Agence
              </Button>
            </div>

          <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterAgences}
              rowKey='id'
              rowClassName='style-row'
              size='small'
              loading={isLoading}
              style={{ zoom: 0.9 }}
              scroll={{ x: 720 }}
              pagination={false}
              onRow={(row, rowIndex) => {
                return {
                  onClick: (event) => {
                    history.push(ROUTES.PANEL.ADMIN.AGENCE_USERS, {
                      agence: row,
                      users: users.filter((u) => u.agence === row.url),
                    });
                  }, // click row
                  onDoubleClick: (event) => {}, // double click row
                  onContextMenu: (event) => {}, // right button click row
                  onMouseEnter: (event) => {}, // mouse enter row
                  onMouseLeave: (event) => {}, // mouse leave row
                };
              }}
            />
          </div>
        </div>
        <Drawer
          title='Ajouter une agence'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <AgenceForm
            partenaire={partner}
            setShowDrawer={setShowDrawer}
            getNewAgence={(age) => {
              setAgences([...(agences as AgenceEntity[]), age]);
              setFilterAgences([...(filterAgences as AgenceEntity[]), age]);
            }}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);
